import React from "react";
import { Link } from "react-router-dom";
import { Target, Zap, ShieldCheck, Heart, ArrowRight, Users, MapPin, Award } from "lucide-react";

const VALUES = [
    {
        icon: Zap,
        title: "Speed",
        desc: "We move fast because your project does. Same-day submittals, rapid correction turnaround, and direct jurisdiction access.",
    },
    {
        icon: ShieldCheck,
        title: "Reliability",
        desc: "Licensed professionals, transparent timelines, and no hidden fees. What we promise is what you get.",
    },
    {
        icon: Heart,
        title: "Client-First",
        desc: "One dedicated project manager per client. Real relationships, real answers, real progress.",
    },
];

export default function About() {
    return (
        <div data-testid="page-about" className="bg-white">
            {/* HERO */}
            <section className="bg-[#0A2540] text-white py-24" data-testid="about-hero">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C5A059]">About Us</div>
                        <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
                            Simplifying construction, <span className="text-[#C5A059]">one permit at a time.</span>
                        </h1>
                        <p className="mt-6 text-lg text-white/75 leading-relaxed">
                            EZ Permit Express was founded to end one of construction's biggest frustrations: slow, opaque, and unpredictable permitting. We're a nationwide team of architects, engineers, and permit specialists on a mission to make approvals fast, clear, and stress-free.
                        </p>
                    </div>
                    <div className="aspect-[4/5] w-full overflow-hidden">
                        <img
                            src="https://images.pexels.com/photos/8117466/pexels-photo-8117466.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                            alt="EZ Permit Express team collaboration"
                            className="w-full h-full object-cover"
                        />
                    </div>
                </div>
            </section>

            {/* MISSION */}
            <section className="py-24" data-testid="mission-section">
                <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
                    <div className="inline-flex items-center gap-2 uppercase tracking-[0.2em] text-xs font-semibold text-[#C5A059]">
                        <Target size={14} /> Our Mission
                    </div>
                    <h2 className="mt-4 text-3xl lg:text-4xl font-semibold text-[#0A2540] tracking-tight">
                        To simplify construction and permitting so every builder, developer, and homeowner can bring their vision to life—without losing months to paperwork.
                    </h2>
                    <p className="mt-6 text-[#475569] leading-relaxed max-w-2xl mx-auto">
                        We combine deep jurisdiction expertise, modern project management tools, and a client-first culture to deliver what the industry has been missing: a reliable partner for the pre-construction phase.
                    </p>
                </div>
            </section>

            {/* VALUES */}
            <section className="py-24 bg-[#F8FAFC] border-t border-[#E2E8F0]" data-testid="values-section">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="max-w-2xl">
                        <div className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C5A059]">What we stand for</div>
                        <h2 className="mt-3 text-3xl lg:text-4xl font-semibold text-[#0A2540] tracking-tight">
                            Three principles behind every project
                        </h2>
                    </div>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
                        {VALUES.map((v) => (
                            <div key={v.title} className="bg-white border border-[#E2E8F0] p-8" data-testid={`value-${v.title.toLowerCase()}`}>
                                <div className="w-12 h-12 flex items-center justify-center bg-[#0A2540] text-[#C5A059]">
                                    <v.icon size={22} />
                                </div>
                                <h3 className="mt-6 text-xl font-semibold text-[#0A2540]">{v.title}</h3>
                                <p className="mt-3 text-[#475569] leading-relaxed">{v.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* STATS */}
            <section className="py-24 bg-[#0A2540] text-white" data-testid="stats-section">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-10 text-center">
                    {[
                        { icon: MapPin, k: "50", s: "States Served" },
                        { icon: Award, k: "2,400+", s: "Permits Approved" },
                        { icon: Users, k: "400+", s: "Happy Clients" },
                        { icon: Zap, k: "40%", s: "Faster Turnaround" },
                    ].map((s) => (
                        <div key={s.s}>
                            <s.icon size={28} className="mx-auto text-[#C5A059]" />
                            <div className="mt-4 text-4xl md:text-5xl font-bold font-[Outfit]">{s.k}</div>
                            <div className="mt-2 text-sm uppercase tracking-widest text-white/60">{s.s}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="py-20 bg-white" data-testid="about-cta-section">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <h2 className="text-3xl lg:text-4xl font-semibold text-[#0A2540] tracking-tight">
                        Ready to experience the difference?
                    </h2>
                    <p className="mt-4 text-[#475569]">
                        Join hundreds of developers, contractors, and homeowners who trust EZ Permit Express.
                    </p>
                    <Link
                        to="/contact"
                        className="mt-8 inline-flex items-center gap-2 btn-gold px-7 py-3.5 text-sm font-semibold"
                        data-testid="about-cta-link"
                    >
                        Start Your Project <ArrowRight size={16} />
                    </Link>
                </div>
            </section>
        </div>
    );
}
